import type { MessageDescriptor } from 'react-intl-next';

import { backgroundPaletteTooltipMessages } from '@atlaskit/editor-common/ui-color';

/**
 * Legacy M1 color picker swatches. Keep this as-is for the current rollout when
 * `confluence_native_tabs_m15` is disabled.
 */
export const LEGACY_TAB_COLOR_SWATCHES = [
	null,
	'blue-subtlest',
	'teal-subtlest',
	'green-subtlest',
	'yellow-subtlest',
	'red-subtlest',
	'purple-subtlest',
	'gray-subtler',
	'blue-subtler',
	'teal-subtler',
	'green-subtler',
	'yellow-subtler',
	'red-subtler',
	'purple-subtler',
	'gray-subtle',
	'blue-subtle',
	'teal-subtle',
	'green-subtle',
	'orange-subtle',
	'red-subtle',
	'purple-subtle',
] as const;

/**
 * M1.5 swatches: same visible palette size/order as the new Panel/Table palette,
 * but still stores semantic tab color IDs in tab params.
 *
 * Tabs keeps "No color" as the top-left reset action in place of Panel's White
 * swatch. Row 3 uses `*-subtle` semantic tab IDs, so existing M1 third-row IDs
 * remain selected but visually migrate to the newer Panel/Table bold-row colors
 * when M1.5 is enabled.
 */
export const STANDARD_TAB_COLOR_SWATCHES = [
	null,
	'blue-subtlest',
	'teal-subtlest',
	'green-subtlest',
	'lime-subtlest',
	'yellow-subtlest',
	'orange-subtlest',
	'red-subtlest',
	'magenta-subtlest',
	'purple-subtlest',
	'gray-subtler',
	'blue-subtler',
	'teal-subtler',
	'green-subtler',
	'lime-subtler',
	'yellow-subtler',
	'orange-subtler',
	'red-subtler',
	'magenta-subtler',
	'purple-subtler',
	'gray-subtle',
	'blue-subtle',
	'teal-subtle',
	'green-subtle',
	'lime-subtle',
	'yellow-subtle',
	'orange-subtle',
	'red-subtle',
	'magenta-subtle',
	'purple-subtle',
] as const;

export const LEGACY_TAB_COLOR_COLUMN_COUNT = 7;
export const STANDARD_TAB_COLOR_COLUMN_COUNT = 10;

export type SelectableTabColorKey = Exclude<
	(typeof LEGACY_TAB_COLOR_SWATCHES)[number] | (typeof STANDARD_TAB_COLOR_SWATCHES)[number],
	null
>;
export type TabColorSwatches = readonly (SelectableTabColorKey | null)[];
export type TabColorKey = SelectableTabColorKey;
export type TabColorLabelMessages = Partial<Record<TabColorKey, MessageDescriptor>>;

type StandardThirdRowStyleKey =
	| 'gray-bold'
	| 'blue-bold'
	| 'teal-bold'
	| 'green-bold'
	| 'lime-bold'
	| 'yellow-bold'
	| 'orange-bold'
	| 'red-bold'
	| 'magenta-bold'
	| 'purple-bold';

type TabColorStyleKey = TabColorKey | StandardThirdRowStyleKey;

export const tabColorToPanelColor: Record<TabColorKey, string> = {
	'blue-subtlest': '#DEEBFF',
	'blue-subtler': '#B3D4FF',
	'blue-subtle': '#ADCBFB',
	'teal-subtlest': '#E6FCFF',
	'teal-subtler': '#B3F5FF',
	'teal-subtle': '#B1E4F7',
	'green-subtlest': '#E3FCEF',
	'green-subtler': '#ABF5D1',
	'green-subtle': '#97EDC9',
	'lime-subtlest': '#EFFFD6',
	'lime-subtler': '#D3F1A7',
	'lime-subtle': '#BDE97C',
	'yellow-subtlest': '#FFFAE6',
	'yellow-subtler': '#FFF0B3',
	'yellow-subtle': '#EFDD4E',
	'orange-subtlest': '#FFF5DB',
	'orange-subtler': '#FCE4A6',
	'orange-subtle': '#FBD779',
	'red-subtlest': '#FFEBE6',
	'red-subtler': '#FFBDAD',
	'red-subtle': '#FFB8B2',
	'magenta-subtlest': '#FFECF8',
	'magenta-subtler': '#FDD0EC',
	'magenta-subtle': '#FCB6E1',
	'purple-subtlest': '#EAE6FF',
	'purple-subtler': '#C0B6F2',
	'purple-subtle': '#E3BDFA',
	'gray-subtler': '#F4F5F7',
	'gray-subtle': '#B7B9BE',
};

const standardThirdRowStyleKeys: Partial<Record<TabColorKey, StandardThirdRowStyleKey>> = {
	'gray-subtle': 'gray-bold',
	'blue-subtle': 'blue-bold',
	'teal-subtle': 'teal-bold',
	'green-subtle': 'green-bold',
	'lime-subtle': 'lime-bold',
	'yellow-subtle': 'yellow-bold',
	'orange-subtle': 'orange-bold',
	'red-subtle': 'red-bold',
	'magenta-subtle': 'magenta-bold',
	'purple-subtle': 'purple-bold',
};

const panelColorByTabColor = tabColorToPanelColor as Partial<Record<string, string>>;
const panelColorToTabColor = new Map<string, TabColorKey>(
	Object.entries(tabColorToPanelColor).map(([colorKey, panelColor]) => [
		panelColor.toLowerCase(),
		colorKey as TabColorKey,
	]),
);

const backgroundPaletteLabelMessages = backgroundPaletteTooltipMessages.light as Record<
	string,
	MessageDescriptor
>;

export const STANDARD_TAB_COLOR_LABEL_MESSAGES: TabColorLabelMessages = {};

for (const [colorKey, panelColor] of Object.entries(tabColorToPanelColor)) {
	const message = backgroundPaletteLabelMessages[panelColor.toUpperCase()];
	if (message) {
		STANDARD_TAB_COLOR_LABEL_MESSAGES[colorKey as TabColorKey] = message;
	}
}

export const getTabColorStyleKey = (
	color: TabColorKey,
	useStandardColorPalette = false,
): TabColorStyleKey =>
	useStandardColorPalette ? (standardThirdRowStyleKeys[color] ?? color) : color;

export const getPanelColorForTabColor = (color: unknown): string | undefined => {
	if (typeof color !== 'string') {
		return undefined;
	}

	return panelColorByTabColor[color];
};

export const getTabColorForPanelColor = (panelColor: unknown): TabColorKey | undefined => {
	if (typeof panelColor !== 'string') {
		return undefined;
	}

	return panelColorToTabColor.get(panelColor.toLowerCase());
};
